import { getSessionData } from '@confluence/session-data';
import type { SessionDataType } from '@confluence/session-data';
import { initializeClientFromValues } from '@confluence/statsig-client/entry-points/initializeClientFromValues';
import { initializeClientWithProvider } from '@confluence/statsig-client/entry-points/initializeClientWithProvider';

import { getStatsigBootstrap } from './getStatsigBootstrap';

function getMetadata(name: string) {
	return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || '';
}

let statsigInitialized: () => void;
const statsigInitializedPromise = new Promise<void>((resolve) => {
	statsigInitialized = resolve;
});

export async function waitUntilStatsigInitialized(): Promise<void> {
	return statsigInitializedPromise;
}

export async function getSessionDataAndInitializeStatsig(): Promise<SessionDataType> {
	let sessionData: SessionDataType;

	if (window.__SSR_RENDERED__) {
		// When SSRed getSessionData is synchronous
		sessionData = await getSessionData();
		const { userId, cloudId, orgId, environment } = sessionData;

		const bootstrappedOptions = await getStatsigBootstrap();
		if (bootstrappedOptions) {
			const { clientSdkKey, experimentValues, configCollectionValues, persistedStatsigOverrides } =
				bootstrappedOptions;
			// Although this function returns a promise, it is actually synchronous when experimentValues are provided
			await initializeClientFromValues({
				identifiers: {
					userId,
					tenantId: cloudId || '',
					orgId,
				},
				environment,
				clientSdkKey,
				experimentValues,
				configCollectionValues,
				persistedStatsigOverrides,
			});
		} else {
			await initializeClientWithProvider({ cloudId, userId, environment });
		}
	} else {
		const cloudId = getMetadata('ajs-cloud-id');
		const userId = getMetadata('ajs-atlassian-account-id');
		const environment = getMetadata('ajs-environment');

		if (userId && cloudId && environment) {
			// Initialize client in parallel if we have all the metadata
			const promiseAllResult = await Promise.all([
				getSessionData(),
				// No orgId in the metadata
				initializeClientWithProvider({ cloudId, userId, environment }),
			]);
			sessionData = promiseAllResult[0];
		} else {
			// If we don't have all the info. Eg in standalone mode there is no environment metadata
			// Downgrade to waterfall
			sessionData = await getSessionData();
			const { userId, cloudId, environment } = sessionData;
			await initializeClientWithProvider({ cloudId, userId, environment });
		}
	}

	statsigInitialized();
	return sessionData;
}
